import { lazy, Suspense, useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from './contexts/AuthContext'
import { ConfirmDialogProvider } from './components/ui/ConfirmDialog'
import { ProtectedRoute } from './components/layout/ProtectedRoute'
import { MainLayout } from './components/layout/MainLayout'
import { AdminLayout } from './components/layout/AdminLayout'
import { ScrollToTop } from './components/layout/ScrollToTop'
import { DOWNLOAD_ERROR_EVENT } from './api/download'
import { NotFoundPage } from './pages/NotFoundPage'

// Auth
import { LoginPage } from './pages/auth/LoginPage'
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage'
const ProfilePage = lazy(() => import('./pages/auth/ProfilePage').then(m => ({ default: m.ProfilePage })))
const McpConsentPage = lazy(() => import('./pages/auth/McpConsentPage').then(m => ({ default: m.McpConsentPage })))
// Dashboard
import { DashboardPage } from './pages/dashboard/DashboardPage'

// Properties
const PropertiesIndex = lazy(() => import('./pages/properties/PropertiesIndex').then(m => ({ default: m.PropertiesIndex })))
const PropertyCreate = lazy(() => import('./pages/properties/PropertyCreate').then(m => ({ default: m.PropertyCreate })))
const PropertyDetails = lazy(() => import('./pages/properties/PropertyDetails').then(m => ({ default: m.PropertyDetails })))
const PropertyEdit = lazy(() => import('./pages/properties/PropertyEdit').then(m => ({ default: m.PropertyEdit })))
const PropertyKeysPage = lazy(() => import('./pages/properties/PropertyKeysPage').then(m => ({ default: m.PropertyKeysPage })))
const PropertyHandoffsPage = lazy(() => import('./pages/properties/PropertyHandoffsPage').then(m => ({ default: m.PropertyHandoffsPage })))
// Customers
const CustomersIndex = lazy(() => import('./pages/customers/CustomersIndex').then(m => ({ default: m.CustomersIndex })))
const CustomerCreate = lazy(() => import('./pages/customers/CustomerCreate').then(m => ({ default: m.CustomerCreate })))
const CustomerDetails = lazy(() => import('./pages/customers/CustomerDetails').then(m => ({ default: m.CustomerDetails })))
const CustomerEdit = lazy(() => import('./pages/customers/CustomerEdit').then(m => ({ default: m.CustomerEdit })))
// Requests
const RequestsIndex = lazy(() => import('./pages/requests/RequestsIndex').then(m => ({ default: m.RequestsIndex })))
const RequestCreate = lazy(() => import('./pages/requests/RequestCreate').then(m => ({ default: m.RequestCreate })))
const RequestDetails = lazy(() => import('./pages/requests/RequestDetails').then(m => ({ default: m.RequestDetails })))
const RequestEdit = lazy(() => import('./pages/requests/RequestEdit').then(m => ({ default: m.RequestEdit })))
// Assignments
const AssignmentsIndex = lazy(() => import('./pages/assignments/AssignmentsIndex').then(m => ({ default: m.AssignmentsIndex })))
const AssignmentCreate = lazy(() => import('./pages/assignments/AssignmentCreate').then(m => ({ default: m.AssignmentCreate })))
const AssignmentDetails = lazy(() => import('./pages/assignments/AssignmentDetails').then(m => ({ default: m.AssignmentDetails })))
const AssignmentEdit = lazy(() => import('./pages/assignments/AssignmentEdit').then(m => ({ default: m.AssignmentEdit })))
// Pianificazione
const PlannerPage = lazy(() => import('./pages/planner/PlannerPage').then(m => ({ default: m.PlannerPage })))
const PlannerTrashPage = lazy(() => import('./pages/planner/PlannerTrashPage').then(m => ({ default: m.PlannerTrashPage })))
const PlannerTodayPage = lazy(() => import('./pages/planner/PlannerTodayPage').then(m => ({ default: m.PlannerTodayPage })))
const CalendarSettingsPage = lazy(() => import('./pages/planner/calendar/CalendarSettingsPage').then(m => ({ default: m.CalendarSettingsPage })))
const TodosPage = lazy(() => import('./pages/todos/TodosPage').then(m => ({ default: m.TodosPage })))
// Email Templates
const EmailTemplatesPage = lazy(() => import('./pages/email-templates/EmailTemplatesPage').then(m => ({ default: m.EmailTemplatesPage })))
const EmailTemplateCreate = lazy(() => import('./pages/email-templates/EmailTemplateCreate').then(m => ({ default: m.EmailTemplateCreate })))
const EmailTemplateEdit = lazy(() => import('./pages/email-templates/EmailTemplateEdit').then(m => ({ default: m.EmailTemplateEdit })))
const EmailTemplateDetails = lazy(() => import('./pages/email-templates/EmailTemplateDetails').then(m => ({ default: m.EmailTemplateDetails })))
// Strumenti
const EmailsPage = lazy(() => import('./pages/emails/EmailsPage').then(m => ({ default: m.EmailsPage })))
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage').then(m => ({ default: m.ReportsPage })))
const PortalsPage = lazy(() => import('./pages/portals/PortalsPage').then(m => ({ default: m.PortalsPage })))
const ApiKeysPage = lazy(() => import('./pages/integrations/ApiKeysPage').then(m => ({ default: m.ApiKeysPage })))
const McpSetupPage = lazy(() => import('./pages/integrations/McpSetupPage').then(m => ({ default: m.McpSetupPage })))
const PublicApiDocsPage = lazy(() => import('./pages/integrations/PublicApiDocsPage').then(m => ({ default: m.PublicApiDocsPage })))
const AgencyWebsiteIntegrationPage = lazy(() => import('./pages/integrations/AgencyWebsiteIntegrationPage').then(m => ({ default: m.AgencyWebsiteIntegrationPage })))
const GoogleCalendarSettingsPage = lazy(() => import('./pages/integrations/GoogleCalendarSettingsPage').then(m => ({ default: m.GoogleCalendarSettingsPage })))
const WebhooksPage = lazy(() => import('./pages/integrations/WebhooksPage').then(m => ({ default: m.WebhooksPage })))
const PartnersPage = lazy(() => import('./pages/partners/PartnersPage').then(m => ({ default: m.PartnersPage })))
const PartnerCreate = lazy(() => import('./pages/partners/PartnerCreate').then(m => ({ default: m.PartnerCreate })))
const PartnerEdit = lazy(() => import('./pages/partners/PartnerEdit').then(m => ({ default: m.PartnerEdit })))
const PartnerDetails = lazy(() => import('./pages/partners/PartnerDetails').then(m => ({ default: m.PartnerDetails })))
const VirtualToursPage = lazy(() => import('./pages/virtual-tours/VirtualToursPage').then(m => ({ default: m.VirtualToursPage })))
const VirtualTourCreate = lazy(() => import('./pages/virtual-tours/VirtualTourCreate').then(m => ({ default: m.VirtualTourCreate })))
const VirtualTourEdit = lazy(() => import('./pages/virtual-tours/VirtualTourEdit').then(m => ({ default: m.VirtualTourEdit })))
// Agenzia
const AgencySettingsPage = lazy(() => import('./pages/agency/AgencySettingsPage').then(m => ({ default: m.AgencySettingsPage })))
const EmployeesPage = lazy(() => import('./pages/employees/EmployeesPage').then(m => ({ default: m.EmployeesPage })))
const EmployeeDetails = lazy(() => import('./pages/employees/EmployeeDetails').then(m => ({ default: m.EmployeeDetails })))
const EmployeeCreate = lazy(() => import('./pages/employees/EmployeeCreate').then(m => ({ default: m.EmployeeCreate })))
const EmployeeEdit = lazy(() => import('./pages/employees/EmployeeEdit').then(m => ({ default: m.EmployeeEdit })))
const EmployeePermissions = lazy(() => import('./pages/employees/EmployeePermissions').then(m => ({ default: m.EmployeePermissions })))
const EmployeeAbsencesPage = lazy(() => import('./pages/employees/EmployeeAbsencesPage').then(m => ({ default: m.EmployeeAbsencesPage })))
const DeadlinesPage = lazy(() => import('./pages/deadlines/DeadlinesPage').then(m => ({ default: m.DeadlinesPage })))
const LeadsPage = lazy(() => import('./pages/leads/LeadsPage').then(m => ({ default: m.LeadsPage })))
const PropertySelectionsPage = lazy(() => import('./pages/property-selections/PropertySelectionsPage').then(m => ({ default: m.PropertySelectionsPage })))
const PropertySelectionPublicPage = lazy(() => import('./pages/property-selections/PropertySelectionPublicPage').then(m => ({ default: m.PropertySelectionPublicPage })))
const PublicVisitBookingPage = lazy(() => import('./pages/visits/PublicVisitBookingPage').then(m => ({ default: m.PublicVisitBookingPage })))
const BranchesPage = lazy(() => import('./pages/branches/BranchesPage').then(m => ({ default: m.BranchesPage })))
const BranchCreate = lazy(() => import('./pages/branches/BranchCreate').then(m => ({ default: m.BranchCreate })))
const BranchEdit = lazy(() => import('./pages/branches/BranchEdit').then(m => ({ default: m.BranchEdit })))
const BranchDetails = lazy(() => import('./pages/branches/BranchDetails').then(m => ({ default: m.BranchDetails })))
// Comunicazioni
const NewsPage = lazy(() => import('./pages/news/NewsPage').then(m => ({ default: m.NewsPage })))
const AnnouncementsPage = lazy(() => import('./pages/announcements/AnnouncementsPage').then(m => ({ default: m.AnnouncementsPage })))
const AnnouncementCreate = lazy(() => import('./pages/announcements/AnnouncementCreate').then(m => ({ default: m.AnnouncementCreate })))
const AnnouncementEdit = lazy(() => import('./pages/announcements/AnnouncementEdit').then(m => ({ default: m.AnnouncementEdit })))
const AnnouncementDetails = lazy(() => import('./pages/announcements/AnnouncementDetails').then(m => ({ default: m.AnnouncementDetails })))
// Altro
const NotificationsPage = lazy(() => import('./pages/notifications/NotificationsPage').then(m => ({ default: m.NotificationsPage })))
const NotificationsSettingsPage = lazy(() => import('./pages/notifications/NotificationsSettingsPage').then(m => ({ default: m.NotificationsSettingsPage })))
const SearchPage = lazy(() => import('./pages/search/SearchPage').then(m => ({ default: m.SearchPage })))
const SearchAdvancedPage = lazy(() => import('./pages/search/SearchAdvancedPage').then(m => ({ default: m.SearchAdvancedPage })))
const ActivityPage = lazy(() => import('./pages/activity/ActivityPage').then(m => ({ default: m.ActivityPage })))
const VisitsPage = lazy(() => import('./pages/visits/VisitsPage').then(m => ({ default: m.VisitsPage })))
const VisitCreate = lazy(() => import('./pages/visits/VisitCreate').then(m => ({ default: m.VisitCreate })))
const VisitEdit = lazy(() => import('./pages/visits/VisitEdit').then(m => ({ default: m.VisitEdit })))
const VisitDetails = lazy(() => import('./pages/visits/VisitDetails').then(m => ({ default: m.VisitDetails })))
const LeasesPage = lazy(() => import('./pages/leases/LeasesPage').then(m => ({ default: m.LeasesPage })))
const LeaseCreate = lazy(() => import('./pages/leases/LeaseCreate').then(m => ({ default: m.LeaseCreate })))
const LeaseEdit = lazy(() => import('./pages/leases/LeaseEdit').then(m => ({ default: m.LeaseEdit })))
const LeaseDetails = lazy(() => import('./pages/leases/LeaseDetails').then(m => ({ default: m.LeaseDetails })))
const OffersPage = lazy(() => import('./pages/offers/OffersPage').then(m => ({ default: m.OffersPage })))
const InvoicesPage = lazy(() => import('./pages/invoices/InvoicesPage').then(m => ({ default: m.InvoicesPage })))
const InvoiceCreate = lazy(() => import('./pages/invoices/InvoiceCreate').then(m => ({ default: m.InvoiceCreate })))
const InvoiceDetails = lazy(() => import('./pages/invoices/InvoiceDetails').then(m => ({ default: m.InvoiceDetails })))
const SignaturesPage = lazy(() => import('./pages/signatures/SignaturesPage').then(m => ({ default: m.SignaturesPage })))
const SignatureCreate = lazy(() => import('./pages/signatures/SignatureCreate').then(m => ({ default: m.SignatureCreate })))
const SignatureDetails = lazy(() => import('./pages/signatures/SignatureDetails').then(m => ({ default: m.SignatureDetails })))
const SignPublicPage = lazy(() => import('./pages/signatures/SignPublicPage').then(m => ({ default: m.SignPublicPage })))
const CompliancePage = lazy(() => import('./pages/compliance/CompliancePage').then(m => ({ default: m.CompliancePage })))
const FilesPage = lazy(() => import('./pages/files/FilesPage').then(m => ({ default: m.FilesPage })))
const DocumentsPage = lazy(() => import('./pages/documents/DocumentsPage').then(m => ({ default: m.DocumentsPage })))
const PropertyOwnersPage = lazy(() => import('./pages/property-owners/PropertyOwnersPage').then(m => ({ default: m.PropertyOwnersPage })))
const PropertyOwnerCreate = lazy(() => import('./pages/property-owners/PropertyOwnerCreate').then(m => ({ default: m.PropertyOwnerCreate })))
const PropertyOwnerEdit = lazy(() => import('./pages/property-owners/PropertyOwnerEdit').then(m => ({ default: m.PropertyOwnerEdit })))
// Wave 6 - Statistics
const PropertyStatistics = lazy(() => import('./pages/properties/PropertyStatistics').then(m => ({ default: m.PropertyStatistics })))
const OfferStatistics = lazy(() => import('./pages/offers/OfferStatistics').then(m => ({ default: m.OfferStatistics })))
const RequestStatistics = lazy(() => import('./pages/requests/RequestStatistics').then(m => ({ default: m.RequestStatistics })))
const AssignmentStatistics = lazy(() => import('./pages/assignments/AssignmentStatistics').then(m => ({ default: m.AssignmentStatistics })))
const CustomerStatistics = lazy(() => import('./pages/customers/CustomerStatistics').then(m => ({ default: m.CustomerStatistics })))
const VisitStatistics = lazy(() => import('./pages/visits/VisitStatistics').then(m => ({ default: m.VisitStatistics })))
// Wave 6 - Customers extra
const CustomerBirthdays = lazy(() => import('./pages/customers/CustomerBirthdays').then(m => ({ default: m.CustomerBirthdays })))
const CustomerPrivacy = lazy(() => import('./pages/customers/CustomerPrivacy').then(m => ({ default: m.CustomerPrivacy })))
// Wave 6 - Agency extra
const AgencyIndexPage = lazy(() => import('./pages/agency/AgencyIndexPage').then(m => ({ default: m.AgencyIndexPage })))
const AgencySubscriptionPage = lazy(() => import('./pages/agency/AgencySubscriptionPage').then(m => ({ default: m.AgencySubscriptionPage })))
// Wave 6 - Portals
const PortalFeedsPage = lazy(() => import('./pages/portals/PortalFeedsPage').then(m => ({ default: m.PortalFeedsPage })))
const PortalStatisticsPage = lazy(() => import('./pages/portals/PortalStatisticsPage').then(m => ({ default: m.PortalStatisticsPage })))
// Wave 6 - Map
const MapPropertiesPage = lazy(() => import('./pages/map/MapPropertiesPage').then(m => ({ default: m.MapPropertiesPage })))
const MapNearbyPage = lazy(() => import('./pages/map/MapNearbyPage').then(m => ({ default: m.MapNearbyPage })))
// Wave 6 - Offers, Visits extra
const OfferCreate = lazy(() => import('./pages/offers/OfferCreate').then(m => ({ default: m.OfferCreate })))
const OfferEdit = lazy(() => import('./pages/offers/OfferEdit').then(m => ({ default: m.OfferEdit })))
const OfferDetails = lazy(() => import('./pages/offers/OfferDetails').then(m => ({ default: m.OfferDetails })))
const OfferPracticesPage = lazy(() => import('./pages/offers/OfferPracticesPage').then(m => ({ default: m.OfferPracticesPage })))
// Wave 6 - System
const ChangelogPage = lazy(() => import('./pages/system/ChangelogPage').then(m => ({ default: m.ChangelogPage })))
const ChangelogDetailsPage = lazy(() => import('./pages/system/ChangelogDetailsPage').then(m => ({ default: m.ChangelogDetailsPage })))
// Wave 6 - FAQ
const FaqPage = lazy(() => import('./pages/faq/FaqPage').then(m => ({ default: m.FaqPage })))
const FaqArticlePage = lazy(() => import('./pages/faq/FaqArticlePage').then(m => ({ default: m.FaqArticlePage })))
const FaqCategoryPage = lazy(() => import('./pages/faq/FaqCategoryPage').then(m => ({ default: m.FaqCategoryPage })))
const FaqCreate = lazy(() => import('./pages/faq/FaqCreate').then(m => ({ default: m.FaqCreate })))
const FaqEdit = lazy(() => import('./pages/faq/FaqEdit').then(m => ({ default: m.FaqEdit })))
// Wave 6 - Knowledge Base
const KnowledgeBasePage = lazy(() => import('./pages/knowledge-base/KnowledgeBasePage').then(m => ({ default: m.KnowledgeBasePage })))
const KnowledgeBaseArticlePage = lazy(() => import('./pages/knowledge-base/KnowledgeBaseArticlePage').then(m => ({ default: m.KnowledgeBaseArticlePage })))
const KnowledgeBaseCreate = lazy(() => import('./pages/knowledge-base/KnowledgeBaseCreate').then(m => ({ default: m.KnowledgeBaseCreate })))
const KnowledgeBaseEdit = lazy(() => import('./pages/knowledge-base/KnowledgeBaseEdit').then(m => ({ default: m.KnowledgeBaseEdit })))
const KnowledgeBaseCategoryPage = lazy(() => import('./pages/knowledge-base/KnowledgeBaseCategoryPage').then(m => ({ default: m.KnowledgeBaseCategoryPage })))
// Wave 7 - Documents, Emails, EmailTemplates
const DocumentStatisticsPage = lazy(() => import('./pages/documents/DocumentStatisticsPage').then(m => ({ default: m.DocumentStatisticsPage })))
const EmailComposePage = lazy(() => import('./pages/emails/EmailComposePage').then(m => ({ default: m.EmailComposePage })))
const EmailTemplatePreviewPage = lazy(() => import('./pages/email-templates/EmailTemplatePreviewPage').then(m => ({ default: m.EmailTemplatePreviewPage })))
// Wave 7 - News, VirtualTours, Timeline
const NewsDetailsPage = lazy(() => import('./pages/news/NewsDetailsPage').then(m => ({ default: m.NewsDetailsPage })))
const VirtualTourPreviewPage = lazy(() => import('./pages/virtual-tours/VirtualTourPreviewPage').then(m => ({ default: m.VirtualTourPreviewPage })))
const TimelineGlobalPage = lazy(() => import('./pages/timeline/TimelineGlobalPage').then(m => ({ default: m.TimelineGlobalPage })))
// Wave 7 - Planner extra
const PlannerEventDetails = lazy(() => import('./pages/planner/PlannerEventDetails').then(m => ({ default: m.PlannerEventDetails })))
const PlannerListPage = lazy(() => import('./pages/planner/PlannerListPage').then(m => ({ default: m.PlannerListPage })))
const PlannerPublicCalendarPage = lazy(() => import('./pages/planner/PlannerPublicCalendarPage').then(m => ({ default: m.PlannerPublicCalendarPage })))
const VisitConfirmPage = lazy(() => import('./pages/visits/VisitConfirmPage').then(m => ({ default: m.VisitConfirmPage })))
const PlannerCreate = lazy(() => import('./pages/planner/PlannerCreate').then(m => ({ default: m.PlannerCreate })))
const PlannerEdit = lazy(() => import('./pages/planner/PlannerEdit').then(m => ({ default: m.PlannerEdit })))
const GuidaPage = lazy(() => import('./pages/guida/GuidaPage').then(m => ({ default: m.GuidaPage })))
const DpaTextPage = lazy(() => import('./pages/legal/DpaTextPage').then(m => ({ default: m.DpaTextPage })))
// Wave 7 - Todos extra
const TodoCreate = lazy(() => import('./pages/todos/TodoCreate').then(m => ({ default: m.TodoCreate })))
const TodoDetails = lazy(() => import('./pages/todos/TodoDetails').then(m => ({ default: m.TodoDetails })))
const TodoEdit = lazy(() => import('./pages/todos/TodoEdit').then(m => ({ default: m.TodoEdit })))
// Wave 7 - Portals CRUD (Create/Edit sono la modale in PortalsPage.tsx, unica implementazione
// mantenuta - le pagine dedicate erano orfane, mai linkate e divergenti nei campi disponibili)
const PortalDetails = lazy(() => import('./pages/portals/PortalDetails').then(m => ({ default: m.PortalDetails })))
// Wave 7 - Files, Requests extra, Reports
const FilesManagerPage = lazy(() => import('./pages/files/FilesManagerPage').then(m => ({ default: m.FilesManagerPage })))
const RequestEmailPreviewPage = lazy(() => import('./pages/requests/RequestEmailPreviewPage').then(m => ({ default: m.RequestEmailPreviewPage })))
const ReportAgentPerformancePage = lazy(() => import('./pages/reports/ReportAgentPerformancePage').then(m => ({ default: m.ReportAgentPerformancePage })))
const ReportFinancialPage = lazy(() => import('./pages/reports/ReportFinancialPage').then(m => ({ default: m.ReportFinancialPage })))
const ReportCustomersPage = lazy(() => import('./pages/reports/ReportCustomersPage').then(m => ({ default: m.ReportCustomersPage })))
const ReportPropertiesPage = lazy(() => import('./pages/reports/ReportPropertiesPage').then(m => ({ default: m.ReportPropertiesPage })))
// Wave 7 - Partners extra
const PartnerCollaborationCreate = lazy(() => import('./pages/partners/PartnerCollaborationCreate').then(m => ({ default: m.PartnerCollaborationCreate })))
// Wave 7 - FAQ, KnowledgeBase manage
const FaqManagePage = lazy(() => import('./pages/faq/FaqManagePage').then(m => ({ default: m.FaqManagePage })))
const KnowledgeBaseManagePage = lazy(() => import('./pages/knowledge-base/KnowledgeBaseManagePage').then(m => ({ default: m.KnowledgeBaseManagePage })))
// Wave 7 - Admin extra
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage').then(m => ({ default: m.AdminDashboardPage })))
const AdminUserResetPassword = lazy(() => import('./pages/admin/users/AdminUserResetPassword').then(m => ({ default: m.AdminUserResetPassword })))
// Admin
const AdminAgenciesPage = lazy(() => import('./pages/admin/agencies/AdminAgenciesPage').then(m => ({ default: m.AdminAgenciesPage })))
const AdminAgencyCreate = lazy(() => import('./pages/admin/agencies/AdminAgencyCreate').then(m => ({ default: m.AdminAgencyCreate })))
const AdminAgencyDetails = lazy(() => import('./pages/admin/agencies/AdminAgencyDetails').then(m => ({ default: m.AdminAgencyDetails })))
const AdminAgencyEdit = lazy(() => import('./pages/admin/agencies/AdminAgencyEdit').then(m => ({ default: m.AdminAgencyEdit })))
const AdminUsersPage = lazy(() => import('./pages/admin/users/AdminUsersPage').then(m => ({ default: m.AdminUsersPage })))
const AdminUserCreate = lazy(() => import('./pages/admin/users/AdminUserCreate').then(m => ({ default: m.AdminUserCreate })))
const AdminUserDetails = lazy(() => import('./pages/admin/users/AdminUserDetails').then(m => ({ default: m.AdminUserDetails })))
const AdminUserEdit = lazy(() => import('./pages/admin/users/AdminUserEdit').then(m => ({ default: m.AdminUserEdit })))
const AdminAnnouncementsPage = lazy(() => import('./pages/admin/announcements/AdminAnnouncementsPage').then(m => ({ default: m.AdminAnnouncementsPage })))
const AdminAnnouncementCreate = lazy(() => import('./pages/admin/announcements/AdminAnnouncementCreate').then(m => ({ default: m.AdminAnnouncementCreate })))
const AdminAnnouncementEdit = lazy(() => import('./pages/admin/announcements/AdminAnnouncementEdit').then(m => ({ default: m.AdminAnnouncementEdit })))
const AdminAnnouncementDetails = lazy(() => import('./pages/admin/announcements/AdminAnnouncementDetails').then(m => ({ default: m.AdminAnnouncementDetails })))
const AdminContentCategoriesPage = lazy(() => import('./pages/admin/content-categories/AdminContentCategoriesPage').then(m => ({ default: m.AdminContentCategoriesPage })))
const AdminChangelogPage = lazy(() => import('./pages/admin/changelog/AdminChangelogPage').then(m => ({ default: m.AdminChangelogPage })))
const AdminSystemLogsPage = lazy(() => import('./pages/admin/system-logs/AdminSystemLogsPage').then(m => ({ default: m.AdminSystemLogsPage })))
const AdminSystemLogDetails = lazy(() => import('./pages/admin/system-logs/AdminSystemLogDetails').then(m => ({ default: m.AdminSystemLogDetails })))
const AdminGestimImportPage = lazy(() => import('./pages/admin/gestim-import/AdminGestimImportPage').then(m => ({ default: m.AdminGestimImportPage })))
const AdminOmiImportPage = lazy(() => import('./pages/admin/omi/AdminOmiImportPage').then(m => ({ default: m.AdminOmiImportPage })))
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      networkMode: 'offlineFirst',
    },
    // Non mettere in pausa le scritture offline: senza una coda persistente esplicita
    // vanno fallite subito, non inviate silenziosamente quando la rete torna.
    mutations: { networkMode: 'always' },
  },
})

function DownloadFailureNotice() {
  const [message, setMessage] = useState('')

  useEffect(() => {
    let timeout: number | undefined
    const onDownloadError = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string }>).detail
      setMessage(detail?.message || 'Non è stato possibile scaricare il file. Riprova.')
      window.clearTimeout(timeout)
      timeout = window.setTimeout(() => setMessage(''), 8000)
    }
    window.addEventListener(DOWNLOAD_ERROR_EVENT, onDownloadError)
    return () => {
      window.removeEventListener(DOWNLOAD_ERROR_EVENT, onDownloadError)
      window.clearTimeout(timeout)
    }
  }, [])

  if (!message) return null
  return (
    <div className="fixed inset-x-4 bottom-4 z-[100] mx-auto flex max-w-lg items-start gap-3 rounded-lg border border-danger-300 bg-danger-50 p-4 text-sm text-danger-900 shadow-lg sm:inset-x-auto sm:right-5" role="alert">
      <span className="min-w-0 flex-1">Download non riuscito: {message}</span>
      <button type="button" className="shrink-0 font-semibold underline" onClick={() => setMessage('')} aria-label="Chiudi avviso download">Chiudi</button>
    </div>
  )
}

export default function App() {
  useEffect(() => {
    const clearPrivateCache = () => queryClient.clear()
    window.addEventListener('domusaurea:clear-private-cache', clearPrivateCache)
    return () => window.removeEventListener('domusaurea:clear-private-cache', clearPrivateCache)
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <DownloadFailureNotice />
        <ScrollToTop />
        <AuthProvider>
          <ConfirmDialogProvider>
          <Suspense fallback={
              <div className="flex items-center justify-center" style={{ minHeight: '60vh' }}>
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" role="status">
                  <span className="sr-only">Caricamento...</span>
                </div>
              </div>
            }>
            <Routes>
            {/* Pagine pubbliche */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/mcp/consent" element={<McpConsentPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/selezione/:token" element={<PropertySelectionPublicPage />} />
            <Route path="/prenota/:token" element={<PublicVisitBookingPage />} />

            {/* App principale (protetta) */}
            <Route element={<ProtectedRoute />}>
              <Route element={<MainLayout />}>
                <Route index element={<DashboardPage />} />

                {/* Properties */}
                <Route path="properties" element={<PropertiesIndex />} />
                <Route path="selections" element={<PropertySelectionsPage />} />
                <Route path="properties/create" element={<PropertyCreate />} />
                <Route path="properties/statistics" element={<PropertyStatistics />} />
                <Route path="property-keys" element={<PropertyKeysPage />} />
                <Route path="property-handoffs" element={<PropertyHandoffsPage />} />
                <Route path="properties/:id" element={<PropertyDetails />} />
                <Route path="properties/:id/edit" element={<PropertyEdit />} />

                {/* Customers */}
                <Route path="customers" element={<CustomersIndex />} />
                <Route path="lead" element={<LeadsPage />} />
                <Route path="customers/create" element={<CustomerCreate />} />
                <Route path="customers/statistics" element={<CustomerStatistics />} />
                <Route path="customers/birthdays" element={<CustomerBirthdays />} />
                <Route path="customers/:id" element={<CustomerDetails />} />
                <Route path="customers/:id/edit" element={<CustomerEdit />} />
                <Route path="customers/:id/privacy" element={<CustomerPrivacy />} />

                {/* Requests */}
                <Route path="requests" element={<RequestsIndex />} />
                <Route path="requests/create" element={<RequestCreate />} />
                <Route path="requests/statistics" element={<RequestStatistics />} />
                <Route path="requests/:id" element={<RequestDetails />} />
                <Route path="requests/:id/edit" element={<RequestEdit />} />
                <Route path="requests/:id/email-preview" element={<RequestEmailPreviewPage />} />

                {/* Assignments */}
                <Route path="assignments" element={<AssignmentsIndex />} />
                <Route path="assignments/create" element={<AssignmentCreate />} />
                <Route path="assignments/statistics" element={<AssignmentStatistics />} />
                <Route path="assignments/:id" element={<AssignmentDetails />} />
                <Route path="assignments/:id/edit" element={<AssignmentEdit />} />

                {/* Fatture (Fase 3B, fatturazione leggera agenzia → cliente) */}
                <Route element={<ProtectedRoute requiredPermission="Permissions.Invoices.View" />}>
                  <Route path="invoices" element={<InvoicesPage />} />
                  <Route path="invoices/:id" element={<InvoiceDetails />} />
                </Route>
                <Route element={<ProtectedRoute requiredPermission="Permissions.Invoices.Manage" />}>
                  <Route path="invoices/create" element={<InvoiceCreate />} />
                </Route>

                {/* Compliance minima antiriciclaggio/KYC (Fase 3D) */}
                <Route element={<ProtectedRoute requiredPermission="Permissions.Customers.View" />}>
                  <Route path="compliance" element={<CompliancePage />} />
                </Route>

                {/* Firme OTP (Fase 3C) */}                <Route element={<ProtectedRoute requiredPermission="Permissions.Signatures.View" />}>
                  <Route path="signatures" element={<SignaturesPage />} />
                  <Route path="signatures/:id" element={<SignatureDetails />} />
                </Route>
                <Route element={<ProtectedRoute requiredPermission="Permissions.Signatures.Manage" />}>
                  <Route path="signatures/create" element={<SignatureCreate />} />
                </Route>

                {/* Comunicazioni */}
                <Route path="news" element={<NewsPage />} />
                <Route path="news/:id" element={<NewsDetailsPage />} />
                <Route path="announcements" element={<AnnouncementsPage />} />
                <Route path="announcements/create" element={<AnnouncementCreate />} />
                <Route path="announcements/:id/edit" element={<AnnouncementEdit />} />
                <Route path="announcements/:id" element={<AnnouncementDetails />} />

                {/* Pianificazione */}
                <Route path="planner" element={<PlannerPage />} />
                <Route path="planner/trash" element={<PlannerTrashPage />} />
                <Route path="planner/today" element={<PlannerTodayPage />} />
                <Route path="planner/list" element={<PlannerListPage />} />
                <Route path="planner/create" element={<PlannerCreate />} />
                <Route path="planner/:id" element={<PlannerEventDetails />} />
                <Route path="planner/:id/edit" element={<PlannerEdit />} />
                <Route path="todos" element={<TodosPage />} />
                <Route path="todos/create" element={<TodoCreate />} />
                <Route path="todos/:id" element={<TodoDetails />} />
                <Route path="todos/:id/edit" element={<TodoEdit />} />

                {/* Strumenti */}
                <Route path="tools/calendar-settings" element={<CalendarSettingsPage />} />
                <Route path="portals" element={<PortalsPage />} />
                <Route path="portals/feeds" element={<PortalFeedsPage />} />
                <Route path="portals/statistics" element={<PortalStatisticsPage />} />
                <Route path="portals/:id" element={<PortalDetails />} />
                <Route element={<ProtectedRoute requiredPermission="Permissions.Agency.Settings" />}>
                  <Route path="integrations/api-keys" element={<ApiKeysPage />} />
                  <Route path="integrations/webhooks" element={<WebhooksPage />} />
                  <Route path="integrations/ai-assistants" element={<McpSetupPage />} />
                  <Route path="integrations/agency-website" element={<AgencyWebsiteIntegrationPage />} />
                </Route>
                <Route path="integrations/api-docs" element={<PublicApiDocsPage />} />
                {/* Connessione Google Calendar: per-utente (ogni agente collega il proprio
                    account), quindi FUORI dal ProtectedRoute Agency.Settings come api-docs. */}
                <Route path="integrations/google-calendar" element={<GoogleCalendarSettingsPage />} />
                <Route path="virtual-tours" element={<VirtualToursPage />} />
                <Route path="virtual-tours/create" element={<VirtualTourCreate />} />
                <Route path="virtual-tours/:id" element={<VirtualTourPreviewPage />} />
                <Route path="virtual-tours/:id/edit" element={<VirtualTourEdit />} />
                <Route path="emails" element={<EmailsPage />} />
                <Route path="emails/compose" element={<EmailComposePage />} />
                <Route path="email-templates" element={<EmailTemplatesPage />} />
                <Route path="email-templates/create" element={<EmailTemplateCreate />} />
                <Route path="email-templates/:id" element={<EmailTemplateDetails />} />
                <Route path="email-templates/:id/edit" element={<EmailTemplateEdit />} />
                <Route path="email-templates/:id/preview" element={<EmailTemplatePreviewPage />} />
                <Route path="partners" element={<PartnersPage />} />
                <Route path="partners/create" element={<PartnerCreate />} />
                <Route path="partners/:id" element={<PartnerDetails />} />
                <Route path="partners/:id/edit" element={<PartnerEdit />} />
                <Route path="partners/:id/collaborations/create" element={<PartnerCollaborationCreate />} />

                {/* Agenzia */}
                <Route path="agency" element={<AgencyIndexPage />} />
                <Route element={<ProtectedRoute requiredPermission="Permissions.Agency.Settings" />}>
                  <Route path="agency/settings" element={<AgencySettingsPage />} />
                </Route>
                <Route path="agency/subscription" element={<AgencySubscriptionPage />} />
                <Route path="employees" element={<EmployeesPage />} />
                <Route path="employee-absences" element={<EmployeeAbsencesPage />} />
                <Route path="deadlines" element={<DeadlinesPage />} />
                <Route path="employees/new" element={<EmployeeCreate />} />
                <Route path="employees/:id" element={<EmployeeDetails />} />
                <Route path="employees/:id/edit" element={<EmployeeEdit />} />
                <Route path="employees/:id/permissions" element={<EmployeePermissions />} />
                <Route path="branches" element={<BranchesPage />} />
                <Route path="branches/new" element={<BranchCreate />} />
                <Route path="branches/:id" element={<BranchDetails />} />
                <Route path="branches/:id/edit" element={<BranchEdit />} />
                <Route path="subscription" element={<AgencySubscriptionPage />} />

                {/* Supporto */}
                <Route path="guida" element={<GuidaPage />} />
                <Route path="legal/nomina-trattamento-dati" element={<DpaTextPage />} />
                <Route path="files" element={<FilesPage />} />
                <Route path="files/manager" element={<FilesManagerPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="reports/agent-performance" element={<ReportAgentPerformancePage />} />
                <Route path="reports/financial" element={<ReportFinancialPage />} />
                <Route path="reports/customers" element={<ReportCustomersPage />} />
                <Route path="reports/properties" element={<ReportPropertiesPage />} />
                <Route path="documents" element={<DocumentsPage />} />
                <Route path="documents/statistics" element={<DocumentStatisticsPage />} />
                <Route path="property-owners" element={<PropertyOwnersPage />} />
                <Route path="property-owners/create" element={<PropertyOwnerCreate />} />
                <Route path="property-owners/:id/edit" element={<PropertyOwnerEdit />} />

                {/* Map */}
                <Route path="map" element={<MapPropertiesPage />} />
                <Route path="map/nearby" element={<MapNearbyPage />} />

                {/* System */}
                <Route path="system/changelog" element={<ChangelogPage />} />
                <Route path="system/changelog/:id" element={<ChangelogDetailsPage />} />

                {/* FAQ */}
                <Route path="faq" element={<FaqPage />} />
                <Route path="faq/create" element={<FaqCreate />} />
                <Route path="faq/manage" element={<FaqManagePage />} />
                <Route path="faq/category/:id" element={<FaqCategoryPage />} />
                <Route path="faq/:id" element={<FaqArticlePage />} />
                <Route path="faq/:id/edit" element={<FaqEdit />} />

                {/* Knowledge Base */}
                <Route path="knowledge-base" element={<KnowledgeBasePage />} />
                <Route path="knowledge-base/create" element={<KnowledgeBaseCreate />} />
                <Route path="knowledge-base/manage" element={<KnowledgeBaseManagePage />} />
                <Route path="knowledge-base/category/:id" element={<KnowledgeBaseCategoryPage />} />
                <Route path="knowledge-base/:id" element={<KnowledgeBaseArticlePage />} />
                <Route path="knowledge-base/:id/edit" element={<KnowledgeBaseEdit />} />

                {/* Timeline */}
                <Route path="timeline" element={<TimelineGlobalPage />} />

                {/* Altro */}
                <Route path="visits" element={<VisitsPage />} />
                <Route path="visits/create" element={<VisitCreate />} />
                <Route path="visits/statistics" element={<VisitStatistics />} />
                <Route path="visits/:id" element={<VisitDetails />} />
                <Route path="visits/:id/edit" element={<VisitEdit />} />
                <Route element={<ProtectedRoute requiredPermission="Permissions.Leases.View" />}>
                  <Route path="leases" element={<LeasesPage />} />
                  <Route path="leases/create" element={<LeaseCreate />} />
                  <Route path="leases/:id" element={<LeaseDetails />} />
                  <Route path="leases/:id/edit" element={<LeaseEdit />} />
                </Route>
                <Route path="offers" element={<OffersPage />} />
                <Route path="offers/create" element={<OfferCreate />} />
                <Route path="offers/statistics" element={<OfferStatistics />} />
                <Route path="offer-practices" element={<OfferPracticesPage />} />
                <Route path="offers/:id" element={<OfferDetails />} />
                <Route path="offers/:id/edit" element={<OfferEdit />} />
                <Route path="search" element={<SearchPage />} />
                <Route path="search/advanced" element={<SearchAdvancedPage />} />
                <Route path="notifications" element={<NotificationsPage />} />
                <Route path="notifications/settings" element={<NotificationsSettingsPage />} />
                <Route path="activity" element={<ActivityPage />} />
                <Route path="profile" element={<ProfilePage />} />
              </Route>
            </Route>

            {/* Calendario pubblico (no auth) */}
            <Route path="planner/public/:token" element={<PlannerPublicCalendarPage />} />

            {/* Conferma visita via link (no auth, token = autenticazione) */}
            <Route path="visite/conferma/:token" element={<VisitConfirmPage />} />

            {/* Firma documento via link + OTP (no auth, token = autenticazione) */}
            <Route path="firma/:token" element={<SignPublicPage />} />

            {/* Admin (protetto, richiede isSuperAdmin: operatore di piattaforma, non il ruolo Admin di una singola agenzia) */}
            <Route element={<ProtectedRoute requireSuperAdmin />}>
              <Route element={<AdminLayout />}>
                <Route path="admin" element={<AdminDashboardPage />} />
                <Route path="admin/agencies" element={<AdminAgenciesPage />} />
                <Route path="admin/agencies/create" element={<AdminAgencyCreate />} />
                <Route path="admin/agencies/:id" element={<AdminAgencyDetails />} />
                <Route path="admin/agencies/:id/edit" element={<AdminAgencyEdit />} />
                <Route path="admin/users" element={<AdminUsersPage />} />
                <Route path="admin/users/create" element={<AdminUserCreate />} />
                <Route path="admin/users/:id" element={<AdminUserDetails />} />
                <Route path="admin/users/:id/edit" element={<AdminUserEdit />} />
                <Route path="admin/users/:id/reset-password" element={<AdminUserResetPassword />} />
                <Route path="admin/announcements" element={<AdminAnnouncementsPage />} />
                <Route path="admin/announcements/create" element={<AdminAnnouncementCreate />} />
                <Route path="admin/announcements/:id/edit" element={<AdminAnnouncementEdit />} />
                <Route path="admin/announcements/:id" element={<AdminAnnouncementDetails />} />
                <Route path="admin/content-categories" element={<AdminContentCategoriesPage />} />
                <Route path="admin/changelog" element={<AdminChangelogPage />} />
                <Route path="admin/system-logs" element={<AdminSystemLogsPage />} />
                <Route path="admin/system-logs/:id" element={<AdminSystemLogDetails />} />
                <Route path="admin/gestim-import" element={<AdminGestimImportPage />} />
                <Route path="admin/omi-import" element={<AdminOmiImportPage />} />
              </Route>
            </Route>

            {/* Percorsi non riconosciuti */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
            </Suspense>
          </ConfirmDialogProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
